import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../stores/auth';
import { cx } from '../lib/cx';
import { IconButton } from './ui';
import { IconClose } from './ui/Icon';
import { HELP_SETS, type HelpIconName } from './helpTexts';

/**
 * Підказки «як користуватись» (картки-слайди): на першому відкритті екрана — у кутку, далі — кнопкою
 * «Як користуватись» у верхній панелі. Тексти — найпростішими словами, по одній дії на слайд.
 * Що вже бачила людина — у localStorage цього браузера (на іншому компʼютері покажеться ще раз — це нормально).
 */
type Icon = HelpIconName;
type HelpSet = (typeof HELP_SETS)[number];
const SETS = HELP_SETS;

const OPEN_EVENT = 'xq:help-open';

/** Відкрити підказки поточного екрана (кнопка «Як користуватись» у верхній панелі). */
export function openHelp(): void {
  window.dispatchEvent(new Event(OPEN_EVENT));
}

export function helpFor(pathname: string): HelpSet | null {
  return SETS.find((s) => s.match(pathname)) ?? null;
}

function seenKey(uid: string | undefined, key: string): string {
  return `xq.help.seen.${uid ?? 'anon'}.${key}`;
}

function wasSeen(k: string): boolean {
  try {
    return localStorage.getItem(k) === '1';
  } catch {
    return true;   // приватний режим: краще не показувати щоразу
  }
}

function markSeen(k: string): void {
  try {
    localStorage.setItem(k, '1');
  } catch {
    /* немає сховища — нічого */
  }
}

export function HelpCards() {
  const { pathname } = useLocation();
  const uid = useAuth((s) => s.me?.user?.id);
  const set = helpFor(pathname);
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);

  // новий екран: показати сам, якщо людина його підказок ще не бачила
  useEffect(() => {
    setI(0);
    setOpen(!!set && !wasSeen(seenKey(uid, set.key)));
  }, [set?.key, uid]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const h = () => {
      setI(0);
      setOpen(true);
    };
    window.addEventListener(OPEN_EVENT, h);
    return () => window.removeEventListener(OPEN_EVENT, h);
  }, []);

  if (!set || !open) return null;
  const slide = set.slides[Math.min(i, set.slides.length - 1)]!;
  const last = i >= set.slides.length - 1;
  const close = () => {
    markSeen(seenKey(uid, set.key));
    setOpen(false);
  };

  return (
    <section
      aria-label={`Як користуватись: ${set.name}`}
      className="fixed z-40 bottom-4 left-4 right-4 sm:right-auto md:left-[260px] sm:w-[380px] bg-bg border border-line rounded-2xl shadow-2xl flex flex-col overflow-hidden print:hidden"
    >
      <div className="h-24 bg-tile flex items-center justify-center relative">
        <span className="w-14 h-14 rounded-2xl bg-bg flex items-center justify-center text-fg">
          <HelpIcon name={slide.icon} />
        </span>
        <IconButton label="Закрити підказки" className="!absolute top-2 right-2 !w-10 !h-10 bg-bg" onClick={close}>
          <IconClose size={16} />
        </IconButton>
      </div>
      <div className="px-5 pt-4 pb-1 flex flex-col gap-2">
        <div className="text-sm font-semibold text-lab">
          {set.name} · крок {i + 1} з {set.slides.length}
        </div>
        <h2 className="m-0 text-lg font-bold leading-snug">{slide.title}</h2>
        <p className="m-0 text-base leading-relaxed">{slide.text}</p>
      </div>
      <div className="flex items-center justify-between gap-3 px-5 pt-3 pb-4">
        <div className="flex gap-1.5" aria-hidden="true">
          {set.slides.map((_, k) => (
            <span key={k} className={cx('h-2 rounded-full transition-all', k === i ? 'w-6 bg-fg' : 'w-2 bg-line-strong')} />
          ))}
        </div>
        <div className="flex gap-2">
          {i > 0 && (
            <button type="button" className="btn h-11 px-4" onClick={() => setI(i - 1)}>
              Назад
            </button>
          )}
          <button type="button" className="btn btn-solid h-11 px-5" onClick={() => (last ? close() : setI(i + 1))}>
            {last ? 'Зрозуміло' : 'Далі'}
          </button>
        </div>
      </div>
    </section>
  );
}

function HelpIcon({ name }: { name: Icon }) {
  const p = { width: 28, height: 28, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 1.8, strokeLinecap: 'round' as const, strokeLinejoin: 'round' as const, 'aria-hidden': true };
  switch (name) {
    case 'plus': return <svg {...p}><path d="M12 5v14M5 12h14" /></svg>;
    case 'clock': return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></svg>;
    case 'case': return <svg {...p}><rect x="3" y="7" width="18" height="13" rx="2" /><path d="M9 7V5a2 2 0 0 1 2-2h2a2 2 0 0 1 2 2v2M3 13h18" /></svg>;
    case 'hand': return <svg {...p}><path d="M7 11V6a1.5 1.5 0 0 1 3 0v4M10 10V4.5a1.5 1.5 0 0 1 3 0V10M13 10V5.5a1.5 1.5 0 0 1 3 0V11M16 9.5a1.5 1.5 0 0 1 3 0V14a7 7 0 0 1-7 7h-1a6 6 0 0 1-5-2.7L4 15.5a1.6 1.6 0 0 1 2.6-1.8L7 14" /></svg>;
    case 'chat': return <svg {...p}><path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.4A8 8 0 1 1 21 12z" /></svg>;
    case 'check': return <svg {...p}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>;
    case 'doc': return <svg {...p}><path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></svg>;
    case 'bug': return <svg {...p}><rect x="7" y="8" width="10" height="12" rx="5" /><path d="M9 8a3 3 0 0 1 6 0M12 12v8M7 13H3.5M20.5 13H17" /></svg>;
    case 'photo': return <svg {...p}><rect x="3" y="5" width="18" height="14" rx="2" /><circle cx="9" cy="10" r="1.6" /><path d="M21 16l-5-5-8 8" /></svg>;
    case 'phone': return <svg {...p}><path d="M5 4h4l2 5-2.5 1.5a11 11 0 0 0 5 5L15 13l5 2v4a2 2 0 0 1-2 2A16 16 0 0 1 3 6a2 2 0 0 1 2-2" /></svg>;
    case 'link': return <svg {...p}><path d="M10 14a4 4 0 0 0 5.7 0l3-3a4 4 0 0 0-5.7-5.7l-1 1M14 10a4 4 0 0 0-5.7 0l-3 3a4 4 0 0 0 5.7 5.7l1-1" /></svg>;
    case 'user': return <svg {...p}><circle cx="12" cy="8" r="4" /><path d="M4 21a8 8 0 0 1 16 0" /></svg>;
    case 'truck': return <svg {...p}><path d="M3 6h11v10H3zM14 9h4l3 3v4h-7" /><circle cx="7" cy="17.5" r="1.8" /><circle cx="17" cy="17.5" r="1.8" /></svg>;
    case 'box': return <svg {...p}><path d="M3 7l9-4 9 4v10l-9 4-9-4z" /><path d="M3 7l9 4 9-4M12 11v10" /></svg>;
    case 'money': return <svg {...p}><rect x="3" y="6" width="18" height="12" rx="2" /><circle cx="12" cy="12" r="2.5" /></svg>;
    case 'chart': return <svg {...p}><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></svg>;
    case 'gear': return <svg {...p}><circle cx="12" cy="12" r="3" /><path d="M12 2v3M12 19v3M2 12h3M19 12h3M4.9 4.9l2.1 2.1M17 17l2.1 2.1M4.9 19.1L7 17M17 7l2.1-2.1" /></svg>;
    case 'globe': return <svg {...p}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3a14 14 0 0 1 0 18M12 3a14 14 0 0 0 0 18" /></svg>;
    case 'calendar': return <svg {...p}><rect x="3" y="5" width="18" height="16" rx="2" /><path d="M3 10h18M8 3v4M16 3v4" /></svg>;
    case 'star': return <svg {...p}><path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" /></svg>;
    case 'shield': return <svg {...p}><path d="M12 3l8 3v6c0 5-3.5 8-8 9-4.5-1-8-4-8-9V6z" /></svg>;
    case 'search': return <svg {...p}><circle cx="11" cy="11" r="6.5" /><path d="M16 16l4.5 4.5" /></svg>;
    case 'bell': return <svg {...p}><path d="M6 16V11a6 6 0 0 1 12 0v5l1.5 2h-15z" /><path d="M10 20a2 2 0 0 0 4 0" /></svg>;
    default: return <svg {...p}><path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" /></svg>;
  }
}
